import { APICore } from "../helpers/api/apiCore";

const api = new APICore();

/**
 * Bridge to the native Moga Delivery WebView shell (mobile-expo/).
 *
 * The shell registers with expo-notifications and injects the resulting Expo
 * push token into `window.__MOGA_NATIVE__` BEFORE the page loads. We forward
 * that token to /mobile-push/subscribe so the backend can push through the
 * Expo Push Service — this is the ONLY notification path that works inside
 * an Android WebView (Web Push / VAPID does not).
 */

export interface NativeAppInfo {
  isNativeApp: boolean;
  platform: "ios" | "android" | "web";
  pushToken: string | null;
  deviceName?: string | null;
  appVersion?: string | null;
}

declare global {
  interface Window {
    __MOGA_NATIVE__?: NativeAppInfo;
  }
}

const isInNativeShell = (): boolean => {
  if (typeof window === "undefined") return false;
  if (window.__MOGA_NATIVE__?.isNativeApp) return true;
  // Fallback: user-agent suffix injected by mobile-expo/App.tsx. Useful before
  // the injected script has run on very early bootstrap paths.
  return typeof navigator !== "undefined" &&
    /MogaDeliveryApp\/[\d.]+/.test(navigator.userAgent || "");
};

/**
 * Wait for the native shell to publish window.__MOGA_NATIVE__.
 * The injection happens before content loads, but there is a slim race window
 * between React mount and the injection completing on cold start.
 */
const waitForNativeInfo = (
  timeoutMs = 4000
): Promise<NativeAppInfo | null> => {
  return new Promise((resolve) => {
    if (window.__MOGA_NATIVE__) {
      resolve(window.__MOGA_NATIVE__);
      return;
    }
    let done = false;
    const finish = (info: NativeAppInfo | null) => {
      if (done) return;
      done = true;
      window.removeEventListener("moga:native-ready", onReady as EventListener);
      resolve(info);
    };
    const onReady = (e: CustomEvent<NativeAppInfo>) => finish(e.detail || null);
    window.addEventListener("moga:native-ready", onReady as EventListener);
    setTimeout(() => finish(window.__MOGA_NATIVE__ || null), timeoutMs);
  });
};

class NativePushService {
  /** True iff the page is running inside the native WebView shell. */
  isNative(): boolean {
    return isInNativeShell();
  }

  /**
   * Register the shell-provided Expo push token with the backend. Idempotent —
   * safe to call on every login/mount; the backend upserts by expoToken.
   * Returns false if we're not in the native app or no token is available yet.
   */
  async subscribe(): Promise<boolean> {
    if (!isInNativeShell()) return false;

    const info = await waitForNativeInfo();
    if (!info?.pushToken) {
      console.warn("[nativePushService] No push token from native shell (permission denied?)");
      return false;
    }

    if (info.platform !== "ios" && info.platform !== "android") {
      // Web fallback — should never happen since isInNativeShell filters
      return false;
    }

    try {
      await api.create("/mobile-push/subscribe", {
        expoToken: info.pushToken,
        platform: info.platform,
        deviceName: info.deviceName || "",
        appVersion: info.appVersion || "",
      });
      console.log("[nativePushService] Expo push token subscribed");
      return true;
    } catch (err: any) {
      console.error("[nativePushService] subscribe failed", err?.message || err);
      return false;
    }
  }

  async unsubscribe(): Promise<void> {
    if (!isInNativeShell()) return;
    const info = window.__MOGA_NATIVE__;
    if (!info?.pushToken) return;
    try {
      await api.create("/mobile-push/unsubscribe", { expoToken: info.pushToken });
    } catch (err) {
      console.error("[nativePushService] unsubscribe failed", err);
    }
  }
}

export const nativePushService = new NativePushService();
